import PropTypes from "prop-types";

/**
 * RealisticSolarInverter – High-fidelity 3D solar inverter component.
 * Features:
 * - Dynamic power flow streams:
 *   1. DC PV Input (Golden flow into top DC input ports)
 *   2. AC Grid Output (Cyan flow out of bottom-right AC conduit)
 *   3. DC Battery Output (Emerald flow out of bottom-left battery conduit)
 * - Flow speed scales dynamically with live generation value (faster when power is higher)
 * - Backlit digital LCD screen with active conversion sine wave
 * - 3 Real status indicator LEDs (POWER, RUN, FAULT)
 */
const RealisticSolarInverter = ({
  liveValue,
  isRunning,
  isOffline,
  statusColor,
  speedScale = 1,
  ratio = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const isFault = statusColor === "red" && !isOffline;
  const val = typeof liveValue === "number" ? liveValue : (isRunning && !isOffline ? 50 : 0);

  // Speed scales strictly with percentage speedScale
  const effectiveSpeed = Math.max(0.3, speedScale || 1);
  const flowDuration = isRunning && !isOffline ? `${Math.max(0.35, 1.2 / effectiveSpeed)}s` : "0s";
  const waveDuration = isRunning && !isOffline ? `${Math.max(0.4, 1.0 / effectiveSpeed)}s` : "0s";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/solar-inverter.png"
        alt="SCADA Realistic Solar Inverter"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 262x308 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 262 308"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="invLcdGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <filter id="invLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <filter id="invFlowGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          {/* Flow Gradients */}
          <linearGradient id="pvFlowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#fbbf24" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="acFlowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="1" />
            <stop offset="100%" stopColor="#0284c7" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="batFlowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4ade80" stopOpacity="1" />
            <stop offset="100%" stopColor="#16a34a" stopOpacity="0.4" />
          </linearGradient>
        </defs>

        {/* ── 1. DC PV Input Power Flow (Top to Inverter Inflow) ── */}
        {isRunning && !isOffline && (
          <g filter="url(#invFlowGlow)">
            {/* Left PV String Flow Line */}
            <path
              d="M 62 10 L 62 48 L 95 48"
              stroke="url(#pvFlowGrad)"
              strokeWidth="2.5"
              strokeDasharray="6 4"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-flow-forward ${flowDuration} linear infinite` }}
            />
            {/* Right PV String Flow Line */}
            <path
              d="M 200 10 L 200 48 L 167 48"
              stroke="url(#pvFlowGrad)"
              strokeWidth="2.5"
              strokeDasharray="6 4"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-flow-forward ${flowDuration} linear infinite` }}
            />
          </g>
        )}

        {/* ── 2. AC Grid Output Flow (Bottom-Right AC Conduit Outflow) ── */}
        {isRunning && !isOffline && (
          <g filter="url(#invFlowGlow)">
            <path
              d="M 182 250 L 182 298 L 245 298"
              stroke="#38bdf8"
              strokeWidth="4.5"
              strokeDasharray="10 5"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-flow-forward ${flowDuration} linear infinite` }}
            />
            {/* Bright underglow */}
            <path
              d="M 182 250 L 182 298 L 245 298"
              stroke="#7dd3fc"
              strokeWidth="8"
              strokeLinecap="round"
              fill="none"
              opacity="0.25"
            />
            {/* Outflow Arrow */}
            <polygon points="240,294 252,298 240,302" fill="#38bdf8" />
          </g>
        )}

        {/* ── 3. Battery Storage Output Flow (Bottom-Left Battery Conduit Outflow) ── */}
        {isRunning && !isOffline && (
          <g filter="url(#invFlowGlow)">
            <path
              d="M 80 250 L 80 298 L 17 298"
              stroke="#4ade80"
              strokeWidth="4.5"
              strokeDasharray="10 5"
              strokeLinecap="round"
              fill="none"
              style={{ animation: `scada-flow-reverse ${flowDuration} linear infinite` }}
            />
            {/* Bright underglow */}
            <path
              d="M 80 250 L 80 298 L 17 298"
              stroke="#86efac"
              strokeWidth="8"
              strokeLinecap="round"
              fill="none"
              opacity="0.25"
            />
            {/* Outflow Arrow */}
            <polygon points="22,294 10,298 22,302" fill="#4ade80" />
          </g>
        )}

        {/* ── Backlit Digital LCD Screen ── */}
        {isRunning && (
          <g filter="url(#invLcdGlow)">
            {/* Green LCD Backlight */}
            <rect x="101" y="83" width="58" height="21" rx="2" fill="#84cc16" opacity="0.3" />

            {/* Animated AC Sine Wave */}
            <path
              d="M 105 93 Q 111 87 117 93 T 129 93 T 141 93 T 153 93"
              stroke="#1e3a5f"
              strokeWidth="1.6"
              fill="none"
              strokeDasharray="14 6"
              style={{ animation: `scada-sine-scroll ${waveDuration} linear infinite` }}
            />
          </g>
        )}

        {/* ── 3 Front Status Indicator LEDs ── */}
        {/* POWER LED */}
        <circle
          cx="96"
          cy="112"
          r="2.5"
          fill={!isOffline ? "#22c55e" : "#64748b"}
          filter={!isOffline ? "url(#invLedGlow)" : "none"}
        />

        {/* RUN LED */}
        <circle
          cx="125"
          cy="112"
          r="2.5"
          fill={isRunning ? "#22c55e" : "#64748b"}
          filter={isRunning ? "url(#invLedGlow)" : "none"}
          style={{
            animation: isRunning ? "scada-led-blink 1.2s infinite ease-in-out" : "none",
          }}
        />

        {/* FAULT LED */}
        <circle
          cx="151"
          cy="112"
          r="2.5"
          fill={isFault ? "#ef4444" : "#475569"}
          filter={isFault ? "url(#invLedGlow)" : "none"}
          style={{
            animation: isFault ? "scada-led-fast-blink 0.8s infinite" : "none",
          }}
        />
      </svg>
    </div>
  );
};

RealisticSolarInverter.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticSolarInverter;
